import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import styled from "styled-components";
import { loginUser } from "../firebase/authService";
import { theme } from "../styles/theme";
import Button from "./Button";

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.gradients.hero};
  padding: ${theme.spacing.xl};
`;

const LoginCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.xl};
  padding: ${theme.spacing['3xl']};
  width: 100%;
  max-width: 400px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.primary};
    border-radius: ${theme.borderRadius.xl} ${theme.borderRadius.xl} 0 0;
  }
`;

const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['2xl']};
`;

const LoginTitle = styled.h2`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const LoginSubtitle = styled.p`
  color: ${theme.colors.secondary[600]};
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.secondary[700]};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.secondary[200]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.secondary[400]};
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: ${theme.colors.primary[600]};
  }
  
  label {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.secondary[600]};
    margin: 0;
  }
`;

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // const { } = useContext(AuthContext); // Login functionality disabled

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    
    try {
      await loginUser(email, password);
    } catch (error) {
      setError(error.message || t('login.errors.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Google login functionality can be added later

  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <LoginTitle>{t('login.title')}</LoginTitle>
          <LoginSubtitle>{t('login.subtitle')}</LoginSubtitle>
        </LoginHeader>
        
        {error && (
          <ErrorMessage>
            <span>⚠️</span>
            {error}
          </ErrorMessage>
        )}
        
        <Form onSubmit={handleLogin}>
          <InputGroup>
            <Label htmlFor="email">{t('login.email')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder={t('login.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputGroup>
          
          <InputGroup>
            <Label htmlFor="password">{t('login.password')}</Label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              placeholder={t('login.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </InputGroup>

          <CheckboxGroup>
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
            />
            <label htmlFor="remember-me">{t('login.rememberMe')}</label>
          </CheckboxGroup>

          <Button
            type="submit"
            variant="primary"
            size="large"
            disabled={isLoading}
            style={{ width: '100%' }}
          >
            {isLoading ? t('login.signingIn') : t('login.signIn')}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;